import { useState, useMemo } from "react";
import { calcRequiredFTE, calcEffectiveHours, calcFutureStateWave, calcFTERelease } from "../../utils/calculations";
import { TrendingUp, Zap, ChevronRight } from "lucide-react";

const WAVES = [
  { n: 1, title: "Wave 1 — Quick Wins", horizon: "0–3 months", prodGain: 8, utilGain: 5, nvaRed: 30, reworkRed: 20, color: "border-sage-300 bg-sage-50", badge: "bg-sage-100 text-sage-700", initiatives: ["Process standardisation & SOP rollout", "Eliminate key dual-handling steps", "Performance dashboard deployment", "Team-level utilisation review"] },
  { n: 2, title: "Wave 2 — Structural", horizon: "3–9 months", prodGain: 18, utilGain: 10, nvaRed: 55, reworkRed: 45, color: "border-amber-200 bg-amber-50", badge: "bg-amber-100 text-amber-700", initiatives: ["Re-engineer top 3 stage workflows", "Deploy automation for routine tasks", "Skills-based routing implementation", "Workforce mix review and rebalancing"] },
  { n: 3, title: "Wave 3 — Transformation", horizon: "9–18 months", prodGain: 30, utilGain: 15, nvaRed: 75, reworkRed: 70, color: "border-blue-200 bg-blue-50", badge: "bg-blue-100 text-blue-700", initiatives: ["Full straight-through processing for eligible volume", "AI/ML decisioning deployment", "Lean capability programme launch", "Continuous improvement culture embedding"] },
];

export default function FutureStateTab({ project, effectiveHours }) {
  const setup = project?.setup || {};
  const stages = project?.stages || [];
  const vc = setup.volume_capacity || {};
  const org = setup.org_profile || {};
  const totalVolume = vc.total_monthly_volume || 0;
  const costPerFTE = vc.cost_per_fte || 15000;
  const eff = effectiveHours || calcEffectiveHours(setup.effective_hours);

  const baselineFTE = useMemo(() =>
    stages.reduce((a, s) => a + calcRequiredFTE(s, totalVolume, eff), 0),
    [stages, totalVolume, eff]
  );

  const waveFTEs = useMemo(() => [1, 2, 3].map((w) => calcFutureStateWave(stages, totalVolume, eff, w)), [stages, totalVolume, eff]);

  // FTE Release Calculator
  const [releaseStageIdx, setReleaseStageIdx] = useState(0);
  const [releaseType, setReleaseType] = useState("both");
  const [releaseMagnitude, setReleaseMagnitude] = useState(15);
  const releaseResult = useMemo(() => {
    if (!stages.length) return null;
    return calcFTERelease(stages[releaseStageIdx], totalVolume, eff, { type: releaseType, magnitude: releaseMagnitude });
  }, [stages, releaseStageIdx, releaseType, releaseMagnitude, totalVolume, eff]);

  return (
    <div className="animate-fade-in">
      <div className="mb-4">
        <p className="text-xs font-semibold uppercase tracking-wider text-sage-500 mb-1">Step 5</p>
        <h2 className="font-heading text-2xl font-semibold text-sage-900">Future State Scenarios</h2>
        <p className="text-sage-500 text-sm mt-1">Projected FTE savings across three lean improvement waves, plus a detailed FTE release calculator</p>
      </div>

      {/* Wave Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
        {WAVES.map((wave, i) => {
          const wFTE = waveFTEs[i];
          const fteSaving = baselineFTE - wFTE;
          const monthlySaving = fteSaving * costPerFTE;
          return (
            <div key={wave.n} className={`rounded-md p-5 border-2 ${wave.color}`} data-testid={`wave-card-${wave.n}`}>
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-heading text-sm font-semibold text-sage-900">{wave.title}</h3>
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${wave.badge}`}>{wave.horizon}</span>
              </div>
              <div className="grid grid-cols-2 gap-3 mb-3">
                <div>
                  <p className="text-xs text-sage-500 mb-0.5">Req FTE</p>
                  <p className="font-heading text-2xl font-bold text-sage-900">{wFTE.toFixed(1)}</p>
                </div>
                <div>
                  <p className="text-xs text-sage-500 mb-0.5">FTE Saving</p>
                  <p className={`font-heading text-2xl font-bold ${fteSaving > 0 ? "text-green-600" : "text-sage-400"}`}>
                    {fteSaving > 0 ? "-" : ""}{Math.abs(fteSaving).toFixed(1)}
                  </p>
                </div>
              </div>
              <div className="text-xs text-sage-600 font-semibold mb-2">
                Monthly saving: <span className="text-green-600">{org.currency || "USD"} {(monthlySaving / 1000).toFixed(0)}K</span>
              </div>
              <div className="text-xs text-sage-500 font-medium mb-2">+{wave.prodGain}% Prod · +{wave.utilGain}% Util</div>
              <ul className="space-y-1 mt-2">
                {wave.initiatives.map((init, ii) => (
                  <li key={ii} className="flex items-start gap-1.5 text-xs text-sage-600">
                    <ChevronRight className="w-3 h-3 mt-0.5 flex-shrink-0 text-sage-400" />
                    {init}
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>

      {/* Savings Trajectory */}
      <div className="bg-white border border-[#E2E8E3] rounded-md p-4 mb-4">
        <h3 className="font-heading text-sm font-semibold text-sage-800 mb-3">Savings Trajectory Summary</h3>
        <div className="grid grid-cols-4 gap-3">
          {[{ label: "Current State", fte: baselineFTE, saving: 0 }, ...WAVES.map((w, i) => ({ label: w.title.split("—")[1]?.trim(), fte: waveFTEs[i], saving: baselineFTE - waveFTEs[i] }))].map(({ label, fte, saving }) => (
            <div key={label} className="text-center bg-sage-50 rounded-md p-3">
              <p className="text-xs text-sage-500 mb-1 leading-tight">{label}</p>
              <p className="font-heading text-xl font-bold text-sage-900">{fte.toFixed(1)}</p>
              <p className="text-xs text-green-600 font-semibold mt-0.5">{saving > 0 ? `-${saving.toFixed(1)} FTE` : "Baseline"}</p>
            </div>
          ))}
        </div>
      </div>

      {/* FTE Release Calculator */}
      <div className="bg-white border border-[#E2E8E3] rounded-md p-5">
        <div className="flex items-center gap-2 mb-4 pb-3 border-b border-sage-50">
          <Zap className="w-4 h-4 text-amber-500" />
          <h3 className="font-heading text-base font-semibold text-sage-800">FTE Release Calculator</h3>
        </div>
        {stages.length === 0 ? (
          <p className="text-sage-400 text-sm text-center py-4">Configure process stages first to use this calculator.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-sage-600 block mb-1.5">Stage</label>
                <select
                  data-testid="release-stage-select"
                  value={releaseStageIdx}
                  onChange={(e) => setReleaseStageIdx(Number(e.target.value))}
                  className="w-full border border-sage-200 rounded-md px-3 py-2 text-sm bg-white focus:border-sage-500 outline-none"
                >
                  {stages.map((s, i) => <option key={i} value={i}>{s.stage_name}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-sage-600 block mb-1.5">Improvement Type</label>
                <div className="flex gap-2">
                  {["productivity", "utilization", "both"].map((t) => (
                    <button
                      key={t}
                      onClick={() => setReleaseType(t)}
                      data-testid={`release-type-${t}`}
                      className={`flex-1 text-xs rounded-md py-1.5 border transition-colors capitalize ${releaseType === t ? "bg-sage-600 text-white border-sage-600" : "border-sage-200 text-sage-600 hover:bg-sage-50"}`}
                    >
                      {t}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-xs font-semibold uppercase tracking-wider text-sage-600">Magnitude</label>
                  <span className="font-heading text-base font-bold text-sage-900">{releaseMagnitude}%</span>
                </div>
                <input
                  data-testid="release-magnitude-slider"
                  type="range" min={1} max={50} value={releaseMagnitude}
                  onChange={(e) => setReleaseMagnitude(Number(e.target.value))}
                  className="w-full"
                />
                <div className="flex justify-between text-xs text-sage-400"><span>1%</span><span>50%</span></div>
              </div>
            </div>

            {releaseResult && (
              <div className="bg-sage-50 rounded-md p-4 space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div><p className="text-xs text-sage-500">Current Req FTE</p><p className="font-heading text-xl font-bold text-sage-900">{releaseResult.currentReqFTE}</p></div>
                  <div><p className="text-xs text-sage-500">New Req FTE</p><p className="font-heading text-xl font-bold text-green-600">{releaseResult.newReqFTE}</p></div>
                  <div><p className="text-xs text-sage-500">Total Release</p><p className="font-heading text-xl font-bold text-sage-900">{releaseResult.releaseTotal} FTE</p></div>
                  <div><p className="text-xs text-sage-500">Monthly Saving</p><p className="font-heading text-base font-bold text-green-600">{org.currency || "USD"} {(parseFloat(releaseResult.releaseTotal) * costPerFTE / 1000).toFixed(0)}K</p></div>
                </div>
                <div className="pt-2 border-t border-sage-200">
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <span className="text-sage-500">Local release: <span className="font-semibold text-sage-800">{parseFloat(releaseResult.releaseLocal).toFixed(1)}</span></span>
                    <span className="text-sage-500">Expat release: <span className="font-semibold text-sage-800">{parseFloat(releaseResult.releaseExpat).toFixed(1)}</span></span>
                    <span className="text-sage-500">Remaining local: <span className="font-semibold text-sage-800">{releaseResult.remainingLocal}</span></span>
                    <span className="text-sage-500">Post-release local%: <span className="font-semibold text-sage-800">{releaseResult.postLocalPct}%</span></span>
                  </div>
                  <div className={`mt-2 text-xs rounded-md px-2 py-1 ${releaseResult.localizationOk ? "bg-green-50 text-green-700" : "bg-red-50 text-red-700"}`}>
                    {releaseResult.localizationOk ? "✓ Localisation target maintained or improved" : "⚠ Warning: Release would breach localisation target"}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
